/**
 * useUndoableTextarea — keeps a textarea uncontrolled (preserving browser undo)
 * while syncing with React state for external reads/updates.
 *
 * React controlled textareas (`value` + `onChange`) destroy the browser undo
 * stack on every render. This hook uses an uncontrolled textarea with a ref
 * and only programmatically sets `.value` when the external state diverges
 * from the user's input (e.g., pipeline load, clear, paste-from-clipboard).
 */
import { useCallback, useEffect, useLayoutEffect,useRef } from 'react';

interface UseUndoableTextareaOptions {
  /** Current value from external state (e.g., Zustand store) */
  value: string;
  /** Callback when the user types — pushes value to external state */
  onChange: (newValue: string) => void;
}

interface UseUndoableTextareaReturn {
  /** Attach this ref to the <textarea> element */
  ref: React.RefObject<HTMLTextAreaElement>;
  /** Props to spread onto the <textarea> — replaces value/onChange */
  textareaProps: {
    defaultValue: string;
    onInput: (e: React.FormEvent<HTMLTextAreaElement>) => void;
  };
}

export function useUndoableTextarea({
  value,
  onChange,
}: UseUndoableTextareaOptions): UseUndoableTextareaReturn {
  const ref = useRef<HTMLTextAreaElement>(null!);
  // Sentinel null = not yet committed; set on first layout effect
  const lastSyncedValue = useRef<string | null>(null);

  // Align ref with committed render value on mount
  useLayoutEffect(() => {
    if (lastSyncedValue.current === null) {
      lastSyncedValue.current = value;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only: syncs ref sentinel once on first render

  // When user types, push to external state without touching the DOM
  const handleInput = useCallback(
    (e: React.FormEvent<HTMLTextAreaElement>) => {
      const newVal = (e.target as HTMLTextAreaElement).value;
      lastSyncedValue.current = newVal;
      onChange(newVal);
    },
    [onChange],
  );

  // Sync external state → DOM only when the change is NOT from user typing
  useEffect(() => {
    if (!ref.current) return;
    if (value !== lastSyncedValue.current) {
      ref.current.value = value;
      lastSyncedValue.current = value;
    }
  }, [value]);

  return {
    ref,
    textareaProps: {
      defaultValue: value,
      onInput: handleInput,
    },
  };
}
